import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FilePlus2, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { BOQ_STATUS, PROCUREMENT_STATUS, dateTimeLabel, type BoqStatus } from '@adisys/shared';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { MetaBadge, Notice, Section, errorText, qtyLabel, type TabProps } from '../../../lib/crm';
import { SearchInput } from '../../../components/DataTable';
import {
  Button, Card, Checkbox, EmptyState, ErrorState, Field, Input, Modal, Select, Skeleton, Textarea, useToast,
} from '../../../components/ui';

interface Line {
  productId: string; productName: string; modelNumber: string | null; brandName: string | null; vendorName: string | null;
  sku: string; specifications: string | null; unit: string; quantity: number; technicalNote: string | null;
  available: number | null; unavailable?: boolean;
}

const fromItem = (i: any): Line => ({
  productId: i.productId, productName: i.productName, modelNumber: i.modelNumber, brandName: i.brandName,
  vendorName: i.vendorName, sku: i.sku, specifications: i.specifications, unit: i.unit,
  quantity: Number(i.quantity), technicalNote: i.technicalNote, available: i.available === null ? null : Number(i.available),
  unavailable: !i.productActive || i.discontinued,
});

export function BoqTab({ project, reload }: TabProps) {
  const { can } = useAuth();
  const toast = useToast();
  const projectId: string = project.id;
  const [selected, setSelected] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [dirty, setDirty] = useState(false);
  const [picker, setPicker] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [dialog, setDialog] = useState<null | 'approve' | 'return' | 'revise'>(null);
  const [text, setText] = useState('');
  const [alsoPricing, setAlsoPricing] = useState(true);

  const list = useQuery({ queryKey: ['crm', 'project', projectId, 'boqs'], queryFn: () => api.get(`/crm/projects/${projectId}/boqs`) });
  const boqs: any[] = list.data?.data ?? [];
  const activeId = selected ?? boqs.find((b) => b.status !== 'superseded')?.id ?? boqs[0]?.id ?? null;

  const detail = useQuery({
    queryKey: ['crm', 'project', projectId, 'boq', activeId],
    queryFn: () => api.get(`/crm/boqs/${activeId}`),
    enabled: Boolean(activeId),
  });
  const boq = detail.data;

  useEffect(() => {
    if (boq) { setLines((boq.items ?? []).map(fromItem)); setDirty(false); }
  }, [boq?.id, boq?.updatedAt, detail.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { void list.refetch(); void detail.refetch(); reload(); };
  const fail = (title: string) => (e: unknown) => toast.error(title, errorText(e));

  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api.post(path, body ?? {}),
    onSuccess: () => { setDialog(null); setText(''); refresh(); },
    onError: fail('That did not work'),
  });
  const save = useMutation({
    mutationFn: () => api.put(`/crm/boqs/${activeId}/items`, {
      items: lines.map((l) => ({ productId: l.productId, quantity: l.quantity, specifications: l.specifications, technicalNote: l.technicalNote })),
    }),
    onSuccess: () => { toast.success('BOQ lines saved'); setDirty(false); refresh(); },
    onError: fail('Could not save the lines'),
  });
  const start = useMutation({
    mutationFn: (reason?: string) => api.post(`/crm/projects/${projectId}/boqs`, reason ? { revisionReason: reason } : {}),
    onSuccess: (r: any) => { setDialog(null); setText(''); setSelected(r.id); refresh(); toast.success('New BOQ started'); },
    onError: fail('Could not start the BOQ'),
  });

  const orderExists = Boolean(project.summary?.order);
  const projectBlocked = project.status !== 'active';
  const stageOk = ['boq', 'pricing', 'quotation', 'approval'].includes(project.stage);
  const isLatest = boq && boqs.find((b) => b.status !== 'superseded')?.id === boq.id;
  const editable = boq?.status === 'draft' && can('crm.boq.manage') && !projectBlocked;
  const status: BoqStatus | undefined = boq?.status;

  if (list.isLoading) return <Skeleton className="h-40 w-full" />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => list.refetch()} />;

  if (!boqs.length) {
    return (
      <EmptyState icon={<FilePlus2 className="h-5 w-5" />} title="No Bill of Quantities yet"
        description={project.stage === 'site_survey'
          ? 'The BOQ starts once the site survey has been submitted to the technical team.'
          : 'Start the BOQ from the approved catalogue, using the submitted site survey as reference.'}
        action={can('crm.boq.manage') && project.stage === 'boq' && !projectBlocked
          ? <Button variant="primary" loading={start.isPending} onClick={() => start.mutate(undefined)}>Start BOQ</Button> : undefined} />
    );
  }

  const update = (idx: number, patch: Partial<Line>) => { setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l))); setDirty(true); };
  const addProducts = (ps: any[]) => {
    setLines((ls) => {
      const next = [...ls];
      for (const p of ps) {
        const at = next.findIndex((l) => l.productId === p.id);
        if (at >= 0) next[at] = { ...next[at]!, quantity: next[at]!.quantity + 1 };
        else next.push({ productId: p.id, productName: p.name, modelNumber: p.modelNumber, brandName: p.brandName, vendorName: p.vendorName,
          sku: p.sku, specifications: p.specifications, unit: p.unit, quantity: 1, technicalNote: null, available: Number(p.available) });
      }
      return next;
    });
    setDirty(true);
  };

  return (
    <div className="space-y-5">
      <div className="no-print flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Field label="Version">
            <Select className="w-52" value={activeId ?? ''} onChange={(e) => { if (!dirty || window.confirm('Discard unsaved changes?')) setSelected(e.target.value); }}>
              {boqs.map((b) => (
                <option key={b.id} value={b.id}>v{b.version} · {BOQ_STATUS.byValue[b.status as BoqStatus].label}{b.status === 'superseded' ? ' (old)' : ''}</option>
              ))}
            </Select>
          </Field>
          {boq && <div className="pt-5"><MetaBadge meta={BOQ_STATUS} value={boq.status} /></div>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>Print BOQ</Button>
          {isLatest && can('crm.boq.manage') && status && status !== 'draft' && !orderExists && stageOk && !projectBlocked && (
            <Button size="sm" onClick={() => { setText(''); setDialog('revise'); }}>New revision</Button>
          )}
        </div>
      </div>

      {projectBlocked && <Notice tone="warning">This project is {project.status.replace('_', ' ')}; the BOQ cannot be changed.</Notice>}
      {orderExists && <Notice tone="info">The customer has approved a quotation, so the BOQ is locked.</Notice>}
      {boq?.reviewComment && status === 'draft' && <Notice tone="warning">Returned for changes: {boq.reviewComment}</Notice>}
      {boq?.revisionReason && <Notice>Revision reason: {boq.revisionReason}</Notice>}
      {status === 'superseded' && <Notice>This version has been replaced by a later revision and is shown for history.</Notice>}
      {status && status !== 'draft' && status !== 'superseded' && !orderExists && (
        <Notice>This BOQ is {BOQ_STATUS.byValue[status].label.toLowerCase()} and cannot be edited. Start a revision to change it — that supersedes the pricing and any quotation not yet accepted.</Notice>
      )}

      {detail.isLoading ? <Skeleton className="h-48 w-full" /> : detail.error ? <ErrorState error={detail.error} onRetry={() => detail.refetch()} /> : boq && (
        <>
          {boq.survey && (
            <Card className="no-print">
              <button className="flex w-full items-center gap-2 text-left text-sm font-semibold text-ink-900" onClick={() => setSurveyOpen((o) => !o)}>
                {surveyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Site survey reference
              </button>
              {surveyOpen && (
                <dl className="mt-3 grid gap-4 text-sm sm:grid-cols-2">
                  {[['Customer requirements', boq.survey.customerRequirements], ['Technical requirements', boq.survey.technicalRequirements],
                    ['Product needs', boq.survey.productNeeds], ['Measurements', boq.survey.measurements], ['Site details', boq.survey.siteDetails]].map(([k, v]) => (
                    <div key={k as string}><dt className="text-xs font-medium text-ink-500">{k}</dt>
                      <dd className="mt-0.5 whitespace-pre-wrap text-ink-900">{(v as string) || '—'}</dd></div>
                  ))}
                </dl>
              )}
            </Card>
          )}

          <Section title={`${boq.title ?? `BOQ v${boq.version}`}`}
            description={`Prepared by ${boq.createdByName ?? '—'} · updated ${dateTimeLabel(boq.updatedAt)}${boq.approvedByName ? ` · approved by ${boq.approvedByName}` : ''}`}
            action={editable && (
              <div className="no-print flex gap-2">
                <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setPicker(true)}>Add products</Button>
                <Button size="sm" variant="primary" icon={<Save className="h-4 w-4" />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Save lines</Button>
              </div>
            )}>
            {dirty && <Notice tone="warning">You have unsaved changes to the lines. Save them before submitting for review.</Notice>}
            <Card padded={false} className="overflow-hidden">
              {lines.length === 0 ? (
                <EmptyState title="No products on this BOQ" description={editable ? 'Add products from the approved catalogue.' : undefined} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead><tr className="border-b border-line bg-sunken/60 text-ink-600">
                      {['#', 'Product', 'Vendor', 'Specifications', 'Qty', 'Technical note', 'Stock'].map((h) => <th key={h} className="px-3 py-2.5 text-sm font-bold">{h}</th>)}
                      {editable && <th className="no-print w-10" />}
                    </tr></thead>
                    <tbody className="divide-y divide-line">
                      {lines.map((l, i) => (
                        <tr key={l.productId} className="align-top">
                          <td className="tabular px-3 py-2.5 text-ink-500">{i + 1}</td>
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-ink-900">{l.productName}</p>
                            <p className="text-xs text-ink-500">{[l.brandName, l.modelNumber, l.sku].filter(Boolean).join(' · ')}</p>
                            {l.unavailable && <p className="text-xs text-danger">No longer available — replace</p>}
                          </td>
                          <td className="px-3 py-2.5 text-ink-700">{l.vendorName ?? '—'}</td>
                          <td className="max-w-[16rem] px-3 py-2.5 text-xs text-ink-700">{l.specifications ?? '—'}</td>
                          <td className="px-3 py-2.5">
                            {editable
                              ? <div className="flex items-center gap-1.5"><Input type="number" min={0.01} step="any" className="w-24" value={l.quantity}
                                  onChange={(e) => update(i, { quantity: Number(e.target.value) })} aria-label="Quantity" /><span className="text-xs text-ink-500">{l.unit}</span></div>
                              : <span className="tabular">{qtyLabel(l.quantity)} {l.unit}</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            {editable
                              ? <Input value={l.technicalNote ?? ''} maxLength={1000} onChange={(e) => update(i, { technicalNote: e.target.value || null })} aria-label="Technical note" />
                              : <span className="text-xs text-ink-700">{l.technicalNote ?? '—'}</span>}
                          </td>
                          <td className="tabular px-3 py-2.5 text-xs text-ink-600">{l.available === null ? '—' : qtyLabel(l.available)}</td>
                          {editable && <td className="no-print px-2 py-2.5">
                            <button className="rounded p-1 text-ink-400 hover:text-danger" aria-label="Remove line"
                              onClick={() => { setLines((ls) => ls.filter((_, k) => k !== i)); setDirty(true); }}><Trash2 className="h-4 w-4" /></button>
                          </td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {boq.technicalNotes && <p className="whitespace-pre-wrap text-sm text-ink-700">{boq.technicalNotes}</p>}
          </Section>

          <div className="no-print flex flex-wrap gap-2">
            {status === 'draft' && can('crm.boq.manage') && !projectBlocked && (
              <Button variant="primary" disabled={dirty || !lines.length} loading={act.isPending}
                onClick={() => act.mutate({ path: `/crm/boqs/${boq.id}/submit-review` })}>Submit for review</Button>
            )}
            {status === 'in_review' && can('crm.boq.approve') && !projectBlocked && (<>
              <Button variant="success" onClick={() => { setText(''); setDialog('approve'); }}>Approve BOQ</Button>
              <Button onClick={() => { setText(''); setDialog('return'); }}>Return for changes</Button>
            </>)}
            {status === 'approved' && (can('crm.boq.manage') || can('crm.boq.approve')) && !projectBlocked && (
              <Button variant="primary" loading={act.isPending} onClick={() => act.mutate({ path: `/crm/boqs/${boq.id}/submit-pricing` })}>Submit for pricing</Button>
            )}
          </div>
        </>
      )}

      <ProductPicker open={picker} onClose={() => setPicker(false)} onAdd={(ps) => { addProducts(ps); setPicker(false); }} />

      <Modal open={dialog === 'approve'} onClose={() => setDialog(null)} title="Approve this BOQ"
        description="You cannot approve a BOQ you prepared yourself."
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="success" loading={act.isPending}
            onClick={() => act.mutate({ path: `/crm/boqs/${activeId}/approve`, body: { submitForPricing: alsoPricing, comment: text || undefined } })}>Approve</Button></>}>
        <div className="space-y-3">
          <Field label="Comment (optional)"><Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} /></Field>
          <Checkbox label="Also submit for pricing" checked={alsoPricing} onChange={(e) => setAlsoPricing(e.target.checked)} />
        </div>
      </Modal>
      <Modal open={dialog === 'return'} onClose={() => setDialog(null)} title="Return the BOQ for changes"
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="primary" disabled={text.trim().length < 3} loading={act.isPending}
            onClick={() => act.mutate({ path: `/crm/boqs/${activeId}/return`, body: { comment: text } })}>Return</Button></>}>
        <Field label="What needs to change?" required><Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} /></Field>
      </Modal>
      <Modal open={dialog === 'revise'} onClose={() => setDialog(null)} title="Start a BOQ revision"
        description="The current pricing, and any quotation the customer has not accepted, will be superseded and the project returns to the BOQ stage."
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="primary" disabled={text.trim().length < 5} loading={start.isPending} onClick={() => start.mutate(text)}>Start revision</Button></>}>
        <Field label="Why is it being revised?" required><Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} /></Field>
      </Modal>
    </div>
  );
}

function ProductPicker({ open, onClose, onAdd }: { open: boolean; onClose: () => void; onAdd: (p: any[]) => void }) {
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [picked, setPicked] = useState<Record<string, any>>({});

  const cats = useQuery({ queryKey: ['crm', 'categories'], queryFn: () => api.get('/crm/categories'), enabled: open, staleTime: 300_000 });
  const products = useQuery({
    queryKey: ['crm', 'products', 'picker', q, categoryId, brandId],
    queryFn: () => api.get('/crm/products', { q: q || undefined, categoryId: categoryId || undefined, brandId: brandId || undefined, active: 'true', size: 50 }),
    enabled: open,
  });
  const brands = useMemo(() => (cats.data?.data ?? []).find((c: any) => c.id === categoryId)?.brands ?? [], [cats.data, categoryId]);
  const rows: any[] = (products.data?.data ?? []).filter((p: any) => !p.discontinued);

  return (
    <Modal open={open} onClose={() => { setPicked({}); onClose(); }} title="Add products from the catalogue" size="xl"
      footer={<><Button onClick={() => { setPicked({}); onClose(); }}>Cancel</Button>
        <Button variant="primary" disabled={!Object.keys(picked).length} onClick={() => { onAdd(Object.values(picked)); setPicked({}); }}>
          Add {Object.keys(picked).length || ''} selected</Button></>}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Name, SKU, model or spec" />
          <Select className="w-48" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setBrandId(''); }} aria-label="Category">
            <option value="">All solutions</option>
            {(cats.data?.data ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select className="w-44" value={brandId} disabled={!categoryId} onChange={(e) => setBrandId(e.target.value)} aria-label="Brand">
            <option value="">All brands</option>
            {brands.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </div>
        {products.isLoading ? <Skeleton className="h-40 w-full" /> : products.error ? <ErrorState error={products.error} onRetry={() => products.refetch()} />
          : rows.length === 0 ? <EmptyState title="No products match" /> : (
          <ul className="max-h-[50vh] divide-y divide-line overflow-y-auto rounded-lg ring-1 ring-line">
            {rows.map((p) => {
              const st = PROCUREMENT_STATUS.byValue[p.procurementStatus as keyof typeof PROCUREMENT_STATUS.byValue];
              return (
                <li key={p.id}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-ink-50">
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-[var(--color-brand-500)]" checked={Boolean(picked[p.id])}
                      onChange={(e) => setPicked((s) => { const n = { ...s }; if (e.target.checked) n[p.id] = p; else delete n[p.id]; return n; })} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink-900">{p.name}</p>
                      <p className="text-xs text-ink-500">{[p.brandName, p.modelNumber, p.sku, p.categoryName].filter(Boolean).join(' · ')}</p>
                      {p.specifications && <p className="mt-0.5 line-clamp-2 text-xs text-ink-600">{p.specifications}</p>}
                    </div>
                    <div className="shrink-0 text-right text-xs">
                      <p className="text-ink-700">{qtyLabel(p.available)} {p.unit} free</p>
                      {st && <p className={st.tone === 'success' ? 'text-success' : st.tone === 'danger' ? 'text-danger' : 'text-warning'}>{st.label}</p>}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
